import { useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Clock, Shield, Award, ArrowRight, Phone } from "lucide-react";
import { HERO, IMG, CONTACT } from "@/data/content";
import { Words } from "@/lib/motion";
import { scrollToId } from "@/components/SmoothScroll";
import Magnetic from "@/components/Magnetic";

const icons = [Clock, Shield, Award];

export default function Hero() {
    const rm = useReducedMotion();
    const ref = useRef(null);

    return (
        <section id="accueil" ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-ink" data-testid="hero-section">
            {/* Background — fixed position, no parallax, no jump */}
            <div className="absolute inset-0 z-0">
                <img
                    src={IMG.hero}
                    alt="Nettoyage professionnel Strasbourg Alsace"
                    className="w-full h-full object-cover"
                    fetchPriority="high"
                />
            </div>
            {/* Dark gradient overlay */}
            <div
                className="absolute inset-0 z-[1]"
                style={{ background: "linear-gradient(180deg, rgba(6,21,41,0.75) 0%, rgba(6,21,41,0.55) 40%, rgba(6,21,41,0.85) 100%)" }}
            />
            <div className="absolute inset-0 z-[1] pointer-events-none" style={{ background: "radial-gradient(60% 50% at 25% 40%, rgba(46,123,255,0.28), transparent 70%)" }} />

            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-28 w-full">
                <div className="max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 mb-7"
                        data-testid="hero-badge"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-electric-soft animate-pulse" />
                        <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-white font-body">{HERO.badge}</span>
                    </motion.div>

                    <p className="text-electric-soft font-display text-lg md:text-2xl font-light mb-5 overflow-hidden">
                        <Words text={HERO.statement} delay={0.35} mount />
                    </p>

                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.08] mb-7 font-display" data-testid="hero-title">
                        <span className="block overflow-hidden"><Words text={HERO.titleLines[0]} delay={0.55} mount /></span>
                        <span className="block overflow-hidden font-semibold"><Words text={HERO.titleLines[1]} delay={0.7} mount /></span>
                        <span className="block overflow-hidden text-[0.62em] text-white/75 mt-1"><Words text={HERO.titleLines[2]} delay={0.9} mount /></span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.8 }}
                        className="text-base md:text-lg text-white/75 leading-relaxed mb-9 max-w-2xl font-body"
                        data-testid="hero-subtitle"
                    >
                        {HERO.subtitle}
                    </motion.p>

                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.25, duration: 0.7 }} className="flex flex-wrap items-center gap-4 mb-14">
                        <Magnetic strength={0.3}>
                            <button onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-3 bg-electric text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" data-testid="hero-cta-devis">
                                Demander un devis
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </Magnetic>
                        <Magnetic strength={0.25}>
                            <button onClick={() => scrollToId("services")} className="inline-flex items-center gap-2 border border-white/40 text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300" data-testid="hero-cta-services">
                                Découvrir nos services
                            </button>
                        </Magnetic>
                        <a href={`tel:${CONTACT.phone}`} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-body px-2" data-testid="hero-cta-appeler">
                            <Phone className="w-4 h-4" /> Appeler maintenant
                        </a>
                    </motion.div>

                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.8 }} className="flex flex-wrap gap-6">
                        {HERO.trust.map((t, i) => {
                            const Icon = icons[i];
                            return (
                                <div key={t} className="flex items-center gap-3">
                                    <div className="w-10 h-10 flex items-center justify-center bg-white/10 backdrop-blur-sm border border-white/10">
                                        <Icon className="w-5 h-5 text-electric-soft" />
                                    </div>
                                    <span className="text-sm text-white/90 font-medium font-body">{t}</span>
                                </div>
                            );
                        })}
                    </motion.div>
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-1 bg-brand-red z-10" />
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-body">Défiler</span>
                <span className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
            </motion.div>
        </section>
    );
}
